
import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { TreeNodeRow } from './TreeNodeRow';
import { SiteNode } from '../types/types';
import { EnergyColumnVisibility } from './EnergyColumnDefinitions';
import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { filterTreeData } from '../utils/tree-filter-utils';
import { SortIndicator } from './molecules/SortIndicator';
import { useDashboardUIStore } from '../stores/dashboard-ui-store';

// Helper function to flatten tree data for pagination while preserving hierarchy
function flattenTreeData(
  nodes: SiteNode[], 
  level = 0, 
  expandedNodes: Set<string> = new Set()
): Array<{ node: SiteNode; level: number; isExpanded: boolean; nodeId: string }> {
  const flattened: Array<{ node: SiteNode; level: number; isExpanded: boolean; nodeId: string }> = [];
  
  nodes.forEach((node) => {
    const nodeId = `${level}-${node.name}-${node.id || ''}`;
    const isExpanded = expandedNodes.has(nodeId);
    
    flattened.push({ node, level, isExpanded, nodeId });
    
    // Only include children if they exist and the parent is expanded
    if (node.children && node.children.length > 0 && isExpanded) {
      flattened.push(...flattenTreeData(node.children, level + 1, expandedNodes));
    }
  });
  
  return flattened;
}

// Helper function to sort tree data recursively
function sortTreeData(
  nodes: SiteNode[],
  column: 'name' | 'sessions' | 'chargingStatus' | null,
  direction: 'asc' | 'desc'
): SiteNode[] {
  if (!column) return nodes;

  const sorted = [...nodes].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (column) {
      case 'name':
        aValue = a.name?.toLowerCase() || '';
        bValue = b.name?.toLowerCase() || '';
        break;
      case 'sessions':
        // For locations: use machinesInSession, for machines: use sessionId presence
        aValue = a.type === 'machine' ? (a.sessionId && a.sessionId !== '0' ? 1 : 0) : (a.machinesInSession || 0);
        bValue = b.type === 'machine' ? (b.sessionId && b.sessionId !== '0' ? 1 : 0) : (b.machinesInSession || 0);
        break;
      case 'chargingStatus':
        aValue = a.status?.toLowerCase() || '';
        bValue = b.status?.toLowerCase() || '';
        break;
      default:
        return 0;
    }

    // Compare values
    if (aValue < bValue) return direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Recursively sort children while preserving hierarchy
  return sorted.map(node => ({
    ...node,
    children: node.children ? sortTreeData(node.children, column, direction) : []
  }));
}

const STORAGE_KEY = 'usageTable_columnWidths_v9'; // Increment version to reset stored widths

  // Responsive column widths based on screen size
const getResponsiveColumnWidths = (screenWidth: number, columnVisibility: EnergyColumnVisibility) => {
  const visibleColumns = Object.values(columnVisibility).filter(Boolean).length;

  // Mobile (< 768px) - Stack important columns only
  if (screenWidth < 768) {
    return {
      tree: columnVisibility.tree ? 60 : 0,
      sessions: columnVisibility.sessions ? 40 : 0,
      chargingStatus: 0, // Hide on mobile
      lastCommunication: 0, // Hide on mobile
      offered: 0, // Hide on mobile
      actual: 0,  // Hide on mobile
      allocated: 0, // Hide on mobile
    };
  }

  // Tablet (768px - 1024px) - Show most important columns
  if (screenWidth < 1024) {
    if (visibleColumns <= 9) {
      return {
        tree: columnVisibility.tree ? 22 : 0,
        sessions: columnVisibility.sessions ? 8 : 0,
        chargingStatus: columnVisibility.chargingStatus ? 11 : 0,
        lastCommunication: columnVisibility.lastCommunication ? 10 : 0,
        offered: columnVisibility.offered ? 8 : 0,
        actual: columnVisibility.actual ? 10 : 0,
        allocated: columnVisibility.allocated ? 31 : 0,
      };
    }
  }

  // Desktop - Show all columns with balanced widths (sum = 100, auto-normalized)
  const baseWidths = {
    tree: 25,
    sessions: 6,
    chargingStatus: 11,
    lastCommunication: 10,
    offered: 7,
    actual: 13,
    allocated: 28,
  };
  
  // Adjust widths based on visible columns
  let totalVisible = 0;
  const adjustedWidths: Record<string, number> = {};
  
  Object.entries(baseWidths).forEach(([key, width]) => {
    if (columnVisibility[key as keyof EnergyColumnVisibility]) {
      adjustedWidths[key] = width;
      totalVisible += width;
    } else {
      adjustedWidths[key] = 0;
    }
  });
  
  // Normalize to 100%
  if (totalVisible > 0) {
    Object.keys(adjustedWidths).forEach(key => {
      if (adjustedWidths[key] > 0) {
        adjustedWidths[key] = (adjustedWidths[key] / totalVisible) * 100;
      }
    });
  }
  
  return adjustedWidths as any; // Type assertion to handle dynamic keys
};

const getStoredColumnWidths = () => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to load stored column widths:', error);
  }
  return null;
};

interface UsageTableProps {
  data: SiteNode[];
  columnVisibility: EnergyColumnVisibility;
  selectedActorId?: string;
  selectedChargingStatuses?: string[];
  selectedAllocatedRanges?: string[];
  isTreeViewVisible?: boolean;
  hideInactive?: boolean;
  /** When true, skip client-side filterTreeData (server already filtered) */
  serverSideFiltered?: boolean;
  onSessionClick?: (sessionId: string | null, machineName?: string, machineId?: number) => void;
  unitMode?: 'A' | 'kW';
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
}

export function UsageTable({
  data,
  columnVisibility,
  selectedActorId,
  selectedChargingStatuses = [],
  selectedAllocatedRanges = [],
  isTreeViewVisible = false,
  hideInactive = false,
  serverSideFiltered = false,
  onSessionClick,
  unitMode = 'A',
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
}: UsageTableProps) {
  // Use store for persistent expand/collapse state
  const expandedNodesArray = useDashboardUIStore((state) => state.expandedNodes);
  const setExpandedNodesArray = useDashboardUIStore((state) => state.setExpandedNodes);
  const toggleNodeExpansion = useDashboardUIStore((state) => state.toggleNodeExpansion);
  const expandAllNodesStore = useDashboardUIStore((state) => state.expandAllNodes);
  const collapseAllNodesStore = useDashboardUIStore((state) => state.collapseAllNodes);
  const resetExpansionState = useDashboardUIStore((state) => state.resetExpansionState);
  const hasUserInteractedWithExpansion = useDashboardUIStore((state) => state.hasUserInteractedWithExpansion);

  // Convert array to Set for component use
  const expandedNodes = useMemo(() => new Set(expandedNodesArray), [expandedNodesArray]);

  const [screenWidth, setScreenWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [sortConfig, setSortConfig] = useState<{
    column: 'name' | 'sessions' | 'chargingStatus' | null;
    direction: 'asc' | 'desc';
  }>({ column: null, direction: 'asc' });
  const [columnWidths, setColumnWidths] = useState(() => {
    const stored = getStoredColumnWidths();
    const defaultWidths = getResponsiveColumnWidths(screenWidth, columnVisibility);
    
    if (stored) {
      // Merge stored widths with any new columns that might have been added
      const merged = {
        ...defaultWidths,
        ...stored,
        // Ensure new columns are included if not in stored
        lastCommunication: stored.lastCommunication ?? defaultWidths.lastCommunication ?? 9,
      };
      return merged;
    }
    return defaultWidths;
  });
  const tableRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isLoadingMoreRef = useRef(isLoadingMore);
  useEffect(() => { isLoadingMoreRef.current = isLoadingMore; }, [isLoadingMore]);

  // Infinite scroll: observe sentinel at bottom of list
  // Use scrollContainerRef as root so intersection is relative to the scroll area,
  // not the viewport. Remove isLoadingMore from deps to avoid observer churn —
  // use a ref instead so the callback always reads the latest value.
  useEffect(() => {
    if (!onLoadMore || !hasMore) return;
    const sentinel = sentinelRef.current;
    const scrollContainer = scrollContainerRef.current;
    if (!sentinel || !scrollContainer) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoadingMoreRef.current) {
          onLoadMore();
        }
      },
      { root: scrollContainer, threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onLoadMore, hasMore]);

  // Apply filters to data (skip when server already filtered)
  const filteredData = useMemo(() => {
    if (serverSideFiltered) return data;
    return filterTreeData(data, selectedChargingStatuses, selectedAllocatedRanges, hideInactive);
  }, [data, selectedChargingStatuses, selectedAllocatedRanges, hideInactive, serverSideFiltered]);

  // Apply sorting to filtered data
  const sortedData = useMemo(() => {
    if (!sortConfig.column) return filteredData;
    return sortTreeData(filteredData, sortConfig.column, sortConfig.direction);
  }, [filteredData, sortConfig]);

  // Handler for column sorting
  const handleSort = (column: 'name' | 'sessions' | 'chargingStatus') => {
    setSortConfig(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle screen resize for responsive columns
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      setScreenWidth(newWidth);
      
      // Update column widths for new screen size if not manually adjusted
      const stored = getStoredColumnWidths();
      if (!stored) {
        setColumnWidths(getResponsiveColumnWidths(newWidth, columnVisibility));
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [columnVisibility]);

  // Update column widths when column visibility changes
  useEffect(() => {
    const stored = getStoredColumnWidths();
    if (!stored) {
      setColumnWidths(getResponsiveColumnWidths(screenWidth, columnVisibility));
    }
  }, [columnVisibility, screenWidth]);

  // Save column widths to session storage whenever they change
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(columnWidths));
    } catch (error) {
      console.warn('Failed to save column widths:', error);
    }
  }, [columnWidths]);

  // Track which dataKey was last auto-expanded to avoid re-expanding on filter/sort changes
  const [lastAutoExpandKey, setLastAutoExpandKey] = useState('');

  // Stable key representing the identity of the top-level dataset
  const dataKey = useMemo(
    () => data.map(n => `${n.id}-${n.name}`).join(','),
    [data]
  );

  // Auto-expand all nodes when the dataset identity changes (new actor/location selected)
  useEffect(() => {
    if (!dataKey || sortedData.length === 0) return;
    if (dataKey === lastAutoExpandKey) return; // Already expanded for this dataset

    const getAllNodeIds = (nodes: SiteNode[], level = 0): string[] => {
      const nodeIds: string[] = [];
      nodes.forEach((node) => {
        const nodeId = `${level}-${node.name}-${node.id || ''}`;
        nodeIds.push(nodeId);
        if (node.children && node.children.length > 0) {
          nodeIds.push(...getAllNodeIds(node.children, level + 1));
        }
      });
      return nodeIds;
    };

    setExpandedNodesArray(getAllNodeIds(sortedData));
    setLastAutoExpandKey(dataKey);
  }, [dataKey, sortedData, lastAutoExpandKey, setExpandedNodesArray]);

  // Flatten the tree data for display
  const flattenedData = useMemo(() => flattenTreeData(sortedData, 0, expandedNodes), [sortedData, expandedNodes]);

  const handleToggleExpand = useCallback((nodeId: string) => {
    toggleNodeExpansion(nodeId); // Use store action
  }, [toggleNodeExpansion]);

  // Get all node IDs for expand/collapse all
  const getAllNodeIds = useCallback((nodes: SiteNode[], level = 0): string[] => {
    const nodeIds: string[] = [];
    nodes.forEach((node) => {
      const nodeId = `${level}-${node.name}-${node.id || ''}`;
      nodeIds.push(nodeId);
      if (node.children && node.children.length > 0) {
        nodeIds.push(...getAllNodeIds(node.children, level + 1));
      }
    });
    return nodeIds;
  }, []);

  const handleExpandAll = useCallback(() => {
    const allNodeIds = getAllNodeIds(sortedData);
    expandAllNodesStore(allNodeIds); // Use store action
  }, [sortedData, getAllNodeIds, expandAllNodesStore]);

  const handleCollapseAll = useCallback(() => {
    collapseAllNodesStore(); // Use store action
  }, [collapseAllNodesStore]);

  // Check if all nodes are expanded or collapsed
  const allNodeIds = useMemo(() => getAllNodeIds(sortedData), [sortedData, getAllNodeIds]);
  const isAllExpanded = allNodeIds.length > 0 && allNodeIds.every(id => expandedNodes.has(id));
  const isAllCollapsed = expandedNodes.size === 0;

  const handleResize = (columnIndex: number, e: React.MouseEvent) => {
    e.preventDefault();
    
    const startX = e.clientX;
    const table = tableRef.current;
    if (!table) return;
    
    const tableRect = table.getBoundingClientRect();
    const columns = Object.keys(columnWidths).filter(key => columnWidths[key as keyof typeof columnWidths] > 0);
    const currentColumn = columns[columnIndex];
    const nextColumn = columns[columnIndex + 1];
    
    if (!currentColumn || !nextColumn) return;
    
    const currentWidth = columnWidths[currentColumn as keyof typeof columnWidths];
    const nextWidth = columnWidths[nextColumn as keyof typeof columnWidths];
    
    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const deltaPercent = (deltaX / tableRect.width) * 100;
      
      const newCurrentWidth = Math.max(5, Math.min(50, currentWidth + deltaPercent));
      const newNextWidth = Math.max(5, Math.min(50, nextWidth - deltaPercent));
      
      setColumnWidths(prev => ({
        ...prev,
        [currentColumn]: newCurrentWidth,
        [nextColumn]: newNextWidth
      }));
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Calculate minimum width based on visible columns
  const visibleColumnCount = Object.values(columnVisibility).filter(Boolean).length;
  const minWidth = screenWidth < 768 ? 'auto' : Math.max(800, visibleColumnCount * 150);

  // Helper function to render resize handle between columns
  const renderResizeHandle = (columnIndex: number) => (
    <div
      className="absolute top-0 w-4 h-full cursor-col-resize flex items-center justify-center z-10"
      style={{ right: '-8px' }}
      onMouseDown={(e) => handleResize(columnIndex, e)}
    >
      <div className="w-0.5 h-6 bg-gray-200/60 hover:bg-blue-300/70 active:bg-blue-400/80 transition-all duration-200 rounded-full" />
    </div>
  );

  // Track which columns are visible for resize handle logic
  const visibleColumnKeys: string[] = [];
  if (columnVisibility.tree && columnWidths.tree > 0) visibleColumnKeys.push('tree');
  if (columnVisibility.sessions && columnWidths.sessions > 0) visibleColumnKeys.push('sessions');
  if (columnVisibility.chargingStatus && columnWidths.chargingStatus > 0) visibleColumnKeys.push('chargingStatus');
  if (columnVisibility.lastCommunication && (columnWidths as any).lastCommunication > 0) visibleColumnKeys.push('lastCommunication');
  if (columnVisibility.offered && columnWidths.offered > 0) visibleColumnKeys.push('offered');
  if (columnVisibility.actual && columnWidths.actual > 0) visibleColumnKeys.push('actual');
  if (columnVisibility.allocated && columnWidths.allocated > 0) visibleColumnKeys.push('allocated');

  return (
    <div className="bg-white rounded-lg border overflow-hidden">
      <div
        className="overflow-x-auto"
        style={{
          minWidth: minWidth,
          maxWidth: '100%'
        }}
      >
        <div ref={tableRef} className="relative" style={{ minWidth: minWidth }}>
          {/* Resizable Header */}
          <div className="flex bg-gray-100 border-b border-l-4 border-l-transparent text-sm font-medium text-gray-700 h-[45px] items-center">
            {columnVisibility.tree && columnWidths.tree > 0 && (
              <div className="relative flex items-center justify-start gap-2" style={{ width: `${columnWidths.tree}%`, paddingLeft: '10px' }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={isAllExpanded ? handleCollapseAll : handleExpandAll}
                  className="h-6 w-6 p-0"
                  title={isAllExpanded ? "Collapse all" : "Expand all"}
                >
                  {isAllExpanded ? (
                    <ChevronsDownUp className="h-4 w-4" />
                  ) : (
                    <ChevronsUpDown className="h-4 w-4" />
                  )}
                </Button>
                <div
                  className="flex items-center gap-1 cursor-pointer hover:bg-gray-200 transition-colors px-2 py-1 rounded"
                  onClick={() => handleSort('name')}
                >
                  <span>Name</span>
                  <SortIndicator column="name" sortConfig={sortConfig} />
                </div>
                {visibleColumnKeys[visibleColumnKeys.indexOf('tree') + 1] && renderResizeHandle(visibleColumnKeys.indexOf('tree'))}
              </div>
            )}

            {columnVisibility.sessions && columnWidths.sessions > 0 && (
              <div
                className="relative flex items-center justify-center gap-1 border-l border-gray-300 cursor-pointer hover:bg-gray-200 transition-colors px-2 rounded"
                style={{ width: `${columnWidths.sessions}%` }}
                onClick={() => handleSort('sessions')}
              >
                <span>Sessions</span>
                <SortIndicator column="sessions" sortConfig={sortConfig} />
                {visibleColumnKeys[visibleColumnKeys.indexOf('sessions') + 1] && renderResizeHandle(visibleColumnKeys.indexOf('sessions'))}
              </div>
            )}

            {columnVisibility.chargingStatus && columnWidths.chargingStatus > 0 && (
              <div
                className="relative flex items-center justify-center gap-1 border-l border-gray-300 cursor-pointer hover:bg-gray-200 transition-colors px-2 rounded"
                style={{ width: `${columnWidths.chargingStatus}%` }}
                onClick={() => handleSort('chargingStatus')}
              >
                <span>Status</span>
                <SortIndicator column="chargingStatus" sortConfig={sortConfig} />
                {visibleColumnKeys[visibleColumnKeys.indexOf('chargingStatus') + 1] && renderResizeHandle(visibleColumnKeys.indexOf('chargingStatus'))}
              </div>
            )}

            {columnVisibility.lastCommunication && (columnWidths as any).lastCommunication > 0 && (
              <div className="relative flex items-center justify-center border-l border-gray-300" style={{ width: `${(columnWidths as any).lastCommunication}%` }}>
                Status Update
                {visibleColumnKeys[visibleColumnKeys.indexOf('lastCommunication') + 1] && renderResizeHandle(visibleColumnKeys.indexOf('lastCommunication'))}
              </div>
            )}

            {columnVisibility.offered && columnWidths.offered > 0 && (
              <div className="relative flex items-center justify-center border-l border-gray-300" style={{ width: `${columnWidths.offered}%` }}>
                Offered [A]
                {visibleColumnKeys[visibleColumnKeys.indexOf('offered') + 1] && renderResizeHandle(visibleColumnKeys.indexOf('offered'))}
              </div>
            )}

            {columnVisibility.actual && columnWidths.actual > 0 && (
              <div className="relative flex items-center justify-center border-l border-gray-300" style={{ width: `${columnWidths.actual}%` }}>
                {unitMode === 'kW' ? 'Actual [kW]' : 'Actual [A]'}
                {visibleColumnKeys[visibleColumnKeys.indexOf('actual') + 1] && renderResizeHandle(visibleColumnKeys.indexOf('actual'))}
              </div>
            )}

            {columnVisibility.allocated && columnWidths.allocated > 0 && (
              <div className="relative flex items-center justify-center border-l border-gray-300" style={{ width: `${columnWidths.allocated}%` }}>
                Allocated / Limit
                {visibleColumnKeys[visibleColumnKeys.indexOf('allocated') + 1] && renderResizeHandle(visibleColumnKeys.indexOf('allocated'))}
              </div>
            )}

          </div>
          
          <div
            ref={scrollContainerRef}
            className="divide-y overflow-y-auto"
            style={{ maxHeight: 'calc(100vh - 280px)' }}
          >
            {flattenedData.map(({ node, level, isExpanded, nodeId }, index) => (
              <TreeNodeRow
                key={`${index}-${nodeId}`}
                node={node}
                level={level}
                hasChildren={node.children && node.children.length > 0}
                isExpanded={isExpanded}
                onToggleExpand={() => handleToggleExpand(nodeId)}
                columnWidths={columnWidths}
                columnVisibility={columnVisibility}
                rowIndex={index}
                isTreeViewVisible={isTreeViewVisible}
                onSessionClick={onSessionClick}
                unitMode={unitMode}
              />
            ))}
            {/* Infinite scroll sentinel */}
            {hasMore && (
              <div ref={sentinelRef} className="h-10 flex items-center justify-center">
                {isLoadingMore && (
                  <span className="text-sm text-gray-400">Loading more...</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
